// Redux
import { AppThunk } from '@redux/configureStore';
import { createSlice } from '@reduxjs/toolkit';
import { setPlan } from '@redux/company/company.slice';
// Types
import {
    ICreatePaymentReponse,
    IInitialPlansState,
    IPlanApiResponse,
    IResponseError,
    IResponseErrorMessage,
    IResponseSuccess,
    IResponseTransactionProps,
} from './types';
// Api
import {
    apiGetActivePlans,
    apiGetTransaction,
    apiGetPlansEmployee,
    apiPostCreateLead,
    apiPostRedeemCode,
    apiGetPromotion,
    apiPostCreateFreeDemoTransaction,
    apiPostCreateTransaction,
    apiPostSendWelcomeNotification,
} from '@api/plans';
// Models
import { IActiveEmployeesPlans, ICompanyPlan, ICreateLead, IFormikValues, IPlanEmployer, IRedeemCodeState } from '@models/Plans';
//Pages
import { INITIAL_SELECTED_PLAN, INVALID_CODE } from '@constants/Plans';
// Utils
import { formatCreateTransactionData, formatPaymentSaveData, refactorRequestData } from './utils';
import { PaymentResponseCodes } from '@constants/responseCodes/payment';
import { TRANSACTION_ERRORS } from '@components/plans-layout/constans/PlansLayout';
import { apiPatchProducStatus } from '@api/company';

const initialState: IInitialPlansState = {
    activePlan: null,
    activePlans: [],
    codeResponse: null,
    codeResponseError: '',
    plansEmployee: [],
    periodicityPlans: [],
    selectedPlan: INITIAL_SELECTED_PLAN,
    statusTransaction: '',
    showPlanLoader: false,
    showTransactionStatus: false,
    electronicPayrollConditions: [],
    paymentUrl: '',
    error: '',
};

const plansSlice = createSlice({
    name: 'plans',
    initialState,
    reducers: {
        setActivePlans: (state, action) => {
            state.activePlans = action.payload;
        },
        setPlansEmployee: (state, { payload }) => {
            state.plansEmployee = payload;
            state.activePlan = payload.find((item: IPlanEmployer) => item.is_active) || null;
        },
        setPeriodicityPlans: (state, action) => {
            state.periodicityPlans = action.payload;
        },
        setSelectedPlan: (state, action) => {
            state.selectedPlan = action.payload;
        },
        setStatusTransaction: (state, action) => {
            state.statusTransaction = action.payload;
        },
        setShowPlanLoader: (state, action) => {
            state.showPlanLoader = action.payload;
        },
        setShowTransactionStatus: (state, action) => {
            state.showTransactionStatus = action.payload;
        },
        setElectronicPayrollConditions: (state, action) => {
            state.electronicPayrollConditions = action.payload;
        },
        failedRequest: (state, action) => {
            state.error = action.payload;
        },
        setCodeResponse: (state, action) => {
            state.codeResponse = action.payload;
        },
        setPaymentUrl: (state, action) => {
            state.paymentUrl = action.payload;
        },
        setCodeResponseError: (state, action) => {
            state.codeResponseError = action.payload;
        },
    },
});

export const getActivePlans = (): AppThunk => {
    return async (dispatch): Promise<void> => {
        const response = await apiGetActivePlans<IActiveEmployeesPlans[]>();
        dispatch(setActivePlans(refactorRequestData(response)));
    };
};

export const getPayments = (): AppThunk<Promise<void>> => {
    return async (dispatch): Promise<void> => {
        const response = await apiGetPlansEmployee<IPlanApiResponse>();
        const plans = response?.data ?? [];
        const currentPlan = plans.find(plan => plan.is_active);
        dispatch(setPlan((currentPlan as ICompanyPlan) || {}));

        if (response.code === PaymentResponseCodes.SUCCESS_OPERATION) {
            resetError();
        }
    };
};

export const getPlansEmployee = (): AppThunk<Promise<void>> => {
    return async (dispatch): Promise<void> => {
        const response = await apiGetPlansEmployee<IPlanApiResponse>();
        const plans = response?.data ?? [];
        dispatch(setPlansEmployee(plans));
    };
};

export const getStatusTransaction = async (): Promise<IResponseTransactionProps> => {
    const response = await apiGetTransaction<IResponseTransactionProps>();
    return response;
};

const resetError = (): AppThunk => {
    return async (dispatch): Promise<void> => {
        dispatch(failedRequest(''));
    };
};

export const createTransaction = ({
    formValues,
    action,
    actionFreeSubscription,
    ip,
}: {
    formValues: IFormikValues;
    action?: () => void;
    actionFreeSubscription?: () => void;
    ip: string;
}): AppThunk => {
    return async (dispatch, getState): Promise<void> => {
        const {
            plans: { selectedPlan, codeResponse },
            company: { information },
        } = getState();

        await resetError();

        try {
            const response = await apiPostCreateTransaction<ICreatePaymentReponse>(
                formatCreateTransactionData({
                    promotionId: codeResponse?.id || '',
                    data: formValues,
                    ip,
                    selectedPlan,
                    dataPayment: formatPaymentSaveData({ formValues, selectedPlan, information }),
                })
            );

            if (response?.code !== PaymentResponseCodes.SUCCESS_OPERATION) {
                dispatch(failedRequest(TRANSACTION_ERRORS.PROCESSING_PAYMENT_ERROR));
                return;
            }

            if (response?.data?.is_free_subscription) {
                actionFreeSubscription?.();
            }

            if (response?.data) {
                dispatch(setPaymentUrl(response.data.url));
                action?.();
            }
        } catch (error) {
            dispatch(failedRequest(error));
        }
    };
};

export const createLead = (data: ICreateLead, action?: () => void): AppThunk => {
    return async (): Promise<void> => {
        try {
            await apiPostCreateLead(data);
            action && action();
        } catch (error) {
            failedRequest(error);
        }
    };
};

export const getPromotion = (periodicityId: string): AppThunk => {
    return async (): Promise<string | undefined> => {
        try {
            const data = (await apiGetPromotion(periodicityId)) as { code: string };
            if (data?.code) return data?.code;
        } catch (error) {
            failedRequest(error);
        }
    };
};

export const createFreeDemoTransaction = (planId: string): AppThunk => {
    return async () => {
        try {
            const createFreeDemoTransactionResponse = await apiPostCreateFreeDemoTransaction({ planId });
            if (createFreeDemoTransactionResponse.data) {
                const statusData = {
                    status: 3,
                };
                await apiPatchProducStatus(statusData);
            }
        } catch (error) {
            failedRequest(error);
        }
    };
};

export const redeemCode = ({
    code,
    plan_id,
    setValidateStep,
    periodicity_id,
    setHandleValidateStep,
    handleOpenModalCoupon,
    handleContinuePurchase,
    isCustomerDiscount = false,
}: IRedeemCodeState): AppThunk => {
    return async (dispatch): Promise<void> => {
        try {
            if ((typeof isCustomerDiscount === 'boolean' && !isCustomerDiscount) || setValidateStep) {
                handleOpenModalCoupon();
                handleContinuePurchase();
                return;
            }

            if (code.length === 0) {
                handleOpenModalCoupon();
                handleContinuePurchase();
                return;
            }

            const response: IResponseSuccess | IResponseErrorMessage | IResponseError = await apiPostRedeemCode({
                code,
                plan_id,
                periodicity_id,
                customer_discount: typeof isCustomerDiscount === 'boolean' && isCustomerDiscount,
            });
            if (typeof response === 'string') {
                dispatch(setCodeResponseError(response));
                dispatch(setCodeResponse(''));
                setHandleValidateStep(false);
                return response;
            }
            if ('errors' in response) {
                dispatch(setCodeResponseError(INVALID_CODE));
                dispatch(setCodeResponse(''));
                setHandleValidateStep(false);
                return;
            }

            dispatch(setCodeResponse(response));
            dispatch(setCodeResponseError(''));
            setHandleValidateStep(true);
        } catch (error) {
            throw new Error(`${error}`);
        }
    };
};

export const sendWelcomeNotification = (userId: string, email: string): AppThunk => {
    return async (): Promise<void> => {
        try {
            await apiPostSendWelcomeNotification({
                user_id: userId,
                email,
            });
        } catch (error) {
            failedRequest(error);
        }
    };
};

/**
 * Action tp check if the plan purchase is blocked based on the configured time window.
 * @returns {boolean} True if purchase is blocked, false otherwise.
 */
export const isPurchasePlanBlocked = (): boolean => {
    const purchaseBlockStart = process.env.REACT_APP_PURCHASE_BLOCK_START;
    const purchaseBlockEnd = process.env.REACT_APP_PURCHASE_BLOCK_END;

    if (!purchaseBlockStart || !purchaseBlockEnd) return false;

    const currentTime = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    return currentTime >= purchaseBlockStart && currentTime <= purchaseBlockEnd;
};

export const {
    setActivePlans,
    setCodeResponse,
    setCodeResponseError,
    setPlansEmployee,
    setPeriodicityPlans,
    setSelectedPlan,
    setShowPlanLoader,
    setShowTransactionStatus,
    failedRequest,
    setStatusTransaction,
    setElectronicPayrollConditions,
    setPaymentUrl,
} = plansSlice.actions;

export default plansSlice.reducer;
